/**
 * Repeatable string-list editor — restrictions ("the agent must never…")
 * and end-call phrases. Add/remove rows; empty rows are dropped on save by
 * normalizeDraft, so mid-edit blanks are harmless.
 */

import { Button } from "../Button";
import { Input } from "../Input";
import { cx } from "../../lib/format";

export interface StringListEditorProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  addLabel: string;
  /** Accessible name prefix for the row inputs, e.g. "Restriction". */
  itemLabel: string;
  /** Mono face for phrase-like values. */
  mono?: boolean;
  disabled?: boolean;
  className?: string;
}

export function StringListEditor({
  value,
  onChange,
  placeholder,
  addLabel,
  itemLabel,
  mono = false,
  disabled = false,
  className,
}: StringListEditorProps) {
  function updateAt(index: number, next: string) {
    onChange(value.map((item, i) => (i === index ? next : item)));
  }

  function removeAt(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  return (
    <div className={cx("flex flex-col gap-2", className)}>
      {value.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            value={item}
            mono={mono}
            disabled={disabled}
            placeholder={placeholder}
            aria-label={`${itemLabel} ${index + 1}`}
            onChange={(event) => updateAt(index, event.target.value)}
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => removeAt(index)}
            aria-label={`Remove ${itemLabel.toLowerCase()} ${index + 1}`}
            className="shrink-0 rounded p-1.5 text-muted transition-colors hover:bg-surface-raised hover:text-bright disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      ))}
      <div>
        <Button size="sm" disabled={disabled} onClick={() => onChange([...value, ""])}>
          {addLabel}
        </Button>
      </div>
    </div>
  );
}
